<template>
  <div class="iq-side-panel-ai-message" data-testid="iq-side-panel-ai-message">
    <span class="iq-side-panel-ai-message__badge" aria-hidden="true">IQ</span>
    <div
      v-if="isError"
      class="iq-side-panel-ai-message__body iq-side-panel-ai-message__error"
      role="alert"
      data-testid="iq-side-panel-ai-message-error"
    >
      <div class="iq-side-panel-ai-message__error-heading">
        <span class="iq-side-panel-ai-message__alert-badge" aria-hidden="true">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 2.5v4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
            <circle cx="6" cy="9" r="0.9" fill="currentColor" />
          </svg>
        </span>
        <p class="iq-side-panel-ai-message__error-title">{{ ERROR_TITLE }}</p>
      </div>
      <p class="iq-side-panel-ai-message__error-text">{{ ERROR_EXPLANATION }}</p>
      <StandardButton
        v-if="canRetry"
        type="magenta"
        variant="outline"
        size="xs"
        label="Retry"
        class="iq-side-panel-ai-message__retry"
        data-testid="iq-side-panel-ai-message-retry"
        @click="emit('retry')"
      />
    </div>
    <div v-else class="iq-side-panel-ai-message__body">
      <div
        class="iq-side-panel-ai-message__text"
        v-html="formattedMessage"
      ></div>
      <div
        v-if="sources.length > 0"
        class="iq-side-panel-ai-message__sources"
        data-testid="iq-side-panel-ai-message-sources"
      >
        <h4 class="iq-side-panel-ai-message__sources-heading">Sources</h4>
        <ol class="iq-side-panel-ai-message__sources-list">
          <li
            v-for="(source, index) in sources"
            :key="sourceListKey(source, index)"
            class="iq-side-panel-ai-message__sources-item"
          >
            <a
              v-if="resolveIQSourceHref(source)"
              :href="resolveIQSourceHref(source)"
              target="_blank"
              rel="noopener noreferrer"
              class="iq-side-panel-ai-message__source-link"
            >
              <span class="iq-side-panel-ai-message__source-icon" aria-hidden="true">
                <Icon name="document_icon" />
              </span>
              <span class="iq-side-panel-ai-message__source-title">{{
                source.title || resolveIQSourceHref(source)
              }}</span>
              <span class="iq-side-panel-ai-message__source-external" aria-hidden="true">
                <Icon name="external_link" />
              </span>
            </a>
            <span v-else class="iq-side-panel-ai-message__source-title">{{
              source.title
            }}</span>
          </li>
        </ol>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import MarkdownIt from "markdown-it";
import DOMPurify from "dompurify";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import { resolveIQSourceHref } from "@components/utils/iqSourceLink";

const md = new MarkdownIt({
  html: false,
  linkify: true,
  breaks: true,
});

const ERROR_TITLE = "IQ couldn't respond";
const ERROR_EXPLANATION =
  "Something went wrong on our end. Your question wasn't lost — you can try again.";

const props = defineProps({
  message: {
    type: String,
    default: "",
  },
  sources: {
    type: Array,
    default: () => [],
  },
  /** Figma State=Error: replaces the answer with the failure copy and Retry. */
  isError: {
    type: Boolean,
    default: false,
  },
  /** Retry is offered only on the newest failed reply. */
  canRetry: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["retry"]);

const formattedMessage = computed(() => {
  const text = props.message || "";
  if (!text) return "";
  return DOMPurify.sanitize(md.render(text));
});

const sourceListKey = (source, index) =>
  source?.page_id || resolveIQSourceHref(source) || `source-${index}`;
</script>

<style scoped>
.iq-side-panel-ai-message {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: 100%;
  background: #ffffff;
}

.iq-side-panel-ai-message__badge {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 25px;
  border-radius: 4px;
  background: #ed2738;
  color: #ffffff;
  font-family: Inter, Helvetica, sans-serif;
  font-size: 8px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1;
}

.iq-side-panel-ai-message__body {
  flex: 1;
  min-width: 0;
}

.iq-side-panel-ai-message__text {
  margin: 0;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 15px;
  line-height: 22px;
  color: #111727;
  word-break: break-word;
}

.iq-side-panel-ai-message__error {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.iq-side-panel-ai-message__error-heading {
  display: flex;
  align-items: center;
  gap: 8px;
}

.iq-side-panel-ai-message__alert-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #d92d20;
  color: #ffffff;
}

.iq-side-panel-ai-message__error-title {
  margin: 0;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 15px;
  line-height: 20px;
  color: #111727;
}

.iq-side-panel-ai-message__error-text {
  margin: 0;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
}

.iq-side-panel-ai-message__retry {
  margin-top: 8px;
}

.iq-side-panel-ai-message__text :deep(p) {
  margin: 0 0 12px 0;
}

.iq-side-panel-ai-message__text :deep(p:last-child) {
  margin-bottom: 0;
}

.iq-side-panel-ai-message__text :deep(li p) {
  margin: 0;
}

.iq-side-panel-ai-message__text :deep(strong) {
  font-weight: 600;
  color: #101828;
}

.iq-side-panel-ai-message__text :deep(ul) {
  margin: 8px 0;
  padding-left: 20px;
  list-style: none;
}

.iq-side-panel-ai-message__text :deep(ul li) {
  position: relative;
  padding-left: 12px;
  margin-bottom: 6px;
}

.iq-side-panel-ai-message__text :deep(ul li::before) {
  content: "•";
  position: absolute;
  left: 0;
  color: #344054;
}

.iq-side-panel-ai-message__text :deep(ol) {
  margin: 8px 0;
  padding-left: 20px;
}

.iq-side-panel-ai-message__text :deep(ol li) {
  margin-bottom: 6px;
}

.iq-side-panel-ai-message__text :deep(a) {
  color: #3b82f6;
  text-decoration: underline;
}

.iq-side-panel-ai-message__sources {
  margin-top: 12px;
}

.iq-side-panel-ai-message__sources-heading {
  margin: 0 0 8px;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #2563eb;
}

.iq-side-panel-ai-message__sources-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  counter-reset: iq-side-panel-source;
}

.iq-side-panel-ai-message__sources-item {
  counter-increment: iq-side-panel-source;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.iq-side-panel-ai-message__sources-item::before {
  content: counter(iq-side-panel-source) ".";
  flex-shrink: 0;
  min-width: 18px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #667085;
}

.iq-side-panel-ai-message__source-link {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  text-decoration: none;
}

.iq-side-panel-ai-message__source-link:hover {
  opacity: 0.85;
}

.iq-side-panel-ai-message__source-icon,
.iq-side-panel-ai-message__source-external {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
}

.iq-side-panel-ai-message__source-title {
  flex: 1;
  min-width: 0;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #2563eb;
  text-decoration: underline;
  word-break: break-word;
}
</style>
